import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import DataTable from "@/components/core/data-table";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { Content } from "@/components/layouts/content";
import {
	useAllUsersQuery,
	useApproveUserMutation,
	useRejectUserMutation,
	useToggleUserStatusMutation,
	useUserApprovalsQuery,
} from "@/features/dashboard/api/queries/user-approval-query";
import type { PendingUser } from "@/features/dashboard/api/services/user-approval-service";
import { formatDate } from "@/utils/format-date";
import { IconCheck, IconEye, IconSearch, IconUserCheck, IconUserOff, IconX } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import type React from "react";

export const Route = createFileRoute("/_dashboard/user-approvals")({
	component: UserApprovalsPage,
});

interface UserApprovalFilters {
	search: string;
	status?: string;
	view: "pending" | "all";
	per_page: number;
}

function UserApprovalsPage() {
	const [filters, setFilters] = useState<UserApprovalFilters>({
		search: "",
		status: undefined,
		view: "pending",
		per_page: 50,
	});

	const [currentPage, setCurrentPage] = useState(1);
	const modal = useModal();

	const { data: pendingData, isLoading: isPendingLoading } = useUserApprovalsQuery(filters.view === "pending" ? { ...filters, page: currentPage } : {});

	const { data: allData, isLoading: isAllLoading } = useAllUsersQuery(filters.view === "all" ? { ...filters, page: currentPage } : {});

	const approveUserMutation = useApproveUserMutation();
	const rejectUserMutation = useRejectUserMutation();
	const toggleUserStatusMutation = useToggleUserStatusMutation();

	const data = filters.view === "pending" ? pendingData : allData;
	const isLoading = filters.view === "pending" ? isPendingLoading : isAllLoading;

	const handleSearch = (value: string) => {
		setFilters({ ...filters, search: value });
		setCurrentPage(1);
	};

	const handleStatusFilter = (value: string) => {
		setFilters({
			...filters,
			status: value === "all" ? undefined : value,
		});
		setCurrentPage(1);
	};

	const handleViewChange = (value: "pending" | "all") => {
		setFilters({ ...filters, view: value });
		setCurrentPage(1);
	};

	const handleApprove = (user: PendingUser, notes?: string, role?: string) => {
		approveUserMutation.mutate({ userId: user.id, notes, role });
	};

	const handleReject = (user: PendingUser) => {
		modal.openModal({
			type: "custom",
			title: "Reject User",
			description: `Reject registration for ${user.first_name} ${user.last_name}`,
			component: <RejectUserModal user={user} onReject={handleReject} />,
		});
	};

	const handleApproveWithNotes = (user: PendingUser) => {
		modal.openModal({
			type: "custom",
			title: "Approve User",
			description: `Approve registration for ${user.first_name} ${user.last_name}`,
			component: <ApproveUserModal user={user} onApprove={handleApprove} />,
		});
	};

	const handleViewDetails = (user: PendingUser) => {
		modal.openModal({
			type: "custom",
			title: "User Details",
			description: "View user registration details",
			component: <UserDetailsModal user={user} />,
		});
	};

	const handleToggleUserStatus = (user: PendingUser) => {
		const isCurrentlyDisabled = user.disabled_at !== null;
		const action = isCurrentlyDisabled ? "enable" : "disable";
		const actionText = isCurrentlyDisabled ? "Enable" : "Disable";

		modal.openModal({
			type: "confirm",
			variant: isCurrentlyDisabled ? "default" : "red",
			title: `${actionText} User Login`,
			description: `Are you sure you want to ${action} login access for ${user.first_name} ${user.last_name}? ${isCurrentlyDisabled ? "The user will be able to log in again." : "The user will not be able to log in until enabled."}`,
			onConfirm: () => {
				toggleUserStatusMutation.mutate(user.id);
			},
		});
	};

	const getStatusBadge = (status: string) => {
		const colorMap: Record<string, string> = {
			pending: "yellow",
			approved: "green",
			rejected: "red",
		};
		return <Badge color={colorMap[status] || "default"}>{status}</Badge>;
	};

	const columns = [
		{
			label: "User",
			accessor: "name",
			cell: (data: PendingUser) => (
				<Link to={`/user-approvals/${data?.data?.id}`} className="flex flex-col font-semibold">
					<div>
						<p className="font-medium">
							{data?.data?.first_name} {data?.data?.last_name}
						</p>
						<p className="text-sm text-gray-500">{data?.data?.email}</p>
					</div>
				</Link>
			),
		},
		{
			label: "Role",
			accessor: "role",
			cell: (user: PendingUser) => <span className="text-sm capitalize">{user.data?.roles?.[0]?.name || "-"}</span>,
		},
		{
			label: "Status",
			accessor: "approval_status",
			cell: (user: PendingUser) => (
				<div className="flex flex-col gap-1">
					{getStatusBadge(user.data?.approval_status)}
					{user.data?.disabled_at && user.data?.approval_status === "approved" && <Badge color="red">Disabled</Badge>}
				</div>
			),
		},
		{
			label: "Registered",
			accessor: "created_at",
			cell: (user: PendingUser) => (
				<div className="text-sm">
					<p>{formatDate(user.data?.created_at, "PP")}</p>
					<p className="text-gray-500">{formatDate(user.data?.created_at, "p")}</p>
				</div>
			),
		},
		{
			label: "Approved By",
			accessor: "approved_by",
			cell: (user: PendingUser) => (
				<div className="text-sm">
					{user.data?.approved_by ? (
						<>
							<p>
								{user.data?.approved_by.first_name} {user.data?.approved_by.last_name}
							</p>
							<p className="text-gray-500">{user.data?.approved_at ? formatDate(user.data.approved_at, "PP") : ""}</p>
						</>
					) : (
						<span className="text-gray-400">-</span>
					)}
				</div>
			),
		},
		{
			label: "Actions",
			accessor: "actions",
			cell: (user: PendingUser) => (
				<div className="flex space-x-2">
					<Button variant="ghost" size="sm" onClick={() => handleViewDetails(user.data)}>
						<IconEye className="h-4 w-4" />
					</Button>
					{user?.data?.approval_status === "pending" && (
						<>
							<Button variant="ghost" size="sm" onClick={() => handleApproveWithNotes(user.data)} className="text-green-600 hover:text-green-700">
								<IconCheck className="h-4 w-4" />
							</Button>
							<Button variant="ghost" size="sm" onClick={() => handleReject(user.data)} className="text-red-600 hover:text-red-700">
								<IconX className="h-4 w-4" />
							</Button>
						</>
					)}
					{user?.data?.approval_status === "approved" && (
						<Button
							variant="ghost"
							size="sm"
							onClick={() => handleToggleUserStatus(user.data)}
							className={user.data?.disabled_at ? "text-green-600 hover:text-green-700" : "text-red-600 hover:text-red-700"}
							title={user.data?.disabled_at ? "Enable user login" : "Disable user login"}
						>
							{user.data?.disabled_at ? <IconUserCheck className="h-4 w-4" /> : <IconUserOff className="h-4 w-4" />}
						</Button>
					)}
				</div>
			),
		},
	];

	return (
		<Content pageName="Users">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">User Approvals</span>
						<span className="text-base text-foreground/70">Manage user registration approvals</span>
					</div>
					{/* Filters */}
					<div className="flex gap-4 items-center">
						<div className="flex gap-2">
							<Button variant={filters.view === "pending" ? "default" : "outline"} onClick={() => handleViewChange("pending")} size="sm">
								Pending ({pendingData?.data?.length || 0})
							</Button>
							<Button variant={filters.view === "all" ? "default" : "outline"} onClick={() => handleViewChange("all")} size="sm">
								All Users
							</Button>
						</div>

						<div className="relative flex-1 max-w-xs">
							<IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
							<Input placeholder="Search users..." value={filters.search} onChange={(e) => handleSearch(e.target.value)} className="pl-10" />
						</div>

						{filters.view === "all" && (
							<Select
								placeholder="Filter by status"
								options={[
									{ value: "all", label: "All Status" },
									{ value: "pending", label: "Pending" },
									{ value: "approved", label: "Approved" },
									{ value: "rejected", label: "Rejected" },
								]}
								value={filters.status || "all"}
								onValueChange={handleStatusFilter}
							/>
						)}
					</div>
				</div>

				<div className="flex flex-col w-full gap-4">
					{/* Table */}
					<DataTable
						columns={columns}
						data={data?.data || []}
						loading={isLoading}
						pagination={
							data?.meta && {
								current: currentPage,
								total: data.meta.total || 0,
								perPage: filters.per_page,
								onChange: setCurrentPage,
							}
						}
					/>
				</div>
			</div>
		</Content>
	);
}

// Modal Components
function ApproveUserModal({ user, onApprove }: { user: PendingUser; onApprove: (user: PendingUser, notes?: string, role?: string) => void }) {
	const [notes, setNotes] = useState("");
	const [selectedRole, setSelectedRole] = useState("salesperson");
	const modal = useModal();
	const { closeModal } = useModalControl();

	const roleOptions = [
		{ value: "admin", label: "Admin" },
		{ value: "salesperson", label: "Salesperson" },
		{ value: "accounting", label: "Accounting" },
	];

	const handleSubmit = () => {
		onApprove(user, notes, selectedRole);

		closeModal();
	};

	return (
		<div className="space-y-4">
			<div>
				<label htmlFor="role-select" className="block text-sm font-medium text-gray-700 mb-2">
					Role Assignment <span className="text-red-500">*</span>
				</label>
				<Select id="role-select" options={roleOptions} value={selectedRole} onValueChange={setSelectedRole} placeholder="Select user role" />
			</div>
			<div>
				<label htmlFor="approval-notes" className="block text-sm font-medium text-gray-700 mb-2">
					Approval Notes (Optional)
				</label>
				<Textarea id="approval-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add any notes about this approval..." rows={3} />
			</div>
			<div className="flex justify-end gap-2">
				<Button variant="outline" onClick={() => modal.closeModal()}>
					Cancel
				</Button>
				<Button onClick={handleSubmit}>Approve User</Button>
			</div>
		</div>
	);
}

function RejectUserModal({ user, onReject }: { user: PendingUser; onReject: (user: PendingUser, notes: string) => void }) {
	const [notes, setNotes] = useState("");
	const modal = useModal();
	const rejectUserMutation = useRejectUserMutation();

	const handleSubmit = () => {
		if (!notes.trim()) {
			return;
		}
		rejectUserMutation.mutate({ userId: user.id, notes });
		modal.closeModal();
	};

	return (
		<div className="space-y-4">
			<div className="bg-red-50 border border-red-200 rounded-md p-4">
				<p className="text-sm text-red-700">
					This will reject the registration for {user.first_name} {user.last_name}. Please provide a reason for the rejection.
				</p>
			</div>
			<div>
				<label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700 mb-2">
					Rejection Reason <span className="text-red-500">*</span>
				</label>
				<Textarea
					id="rejection-reason"
					value={notes}
					onChange={(e) => setNotes(e.target.value)}
					placeholder="Explain why this registration is being rejected..."
					rows={3}
					required
				/>
			</div>
			<div className="flex justify-end gap-2">
				<Button variant="outline" onClick={() => modal.closeModal()}>
					Cancel
				</Button>
				<Button variant="destructive" onClick={handleSubmit} disabled={!notes.trim() || rejectUserMutation.isPending} loading={rejectUserMutation.isPending}>
					Reject User
				</Button>
			</div>
		</div>
	);
}

function UserDetailsModal({ user }: { user: PendingUser }) {
	const modal = useModal();

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-2 gap-4">
				<div>
					<div className="block text-sm font-medium text-gray-700 mb-1">First Name</div>
					<p className="text-sm text-gray-900">{user.first_name}</p>
				</div>
				<div>
					<div className="block text-sm font-medium text-gray-700 mb-1">Last Name</div>
					<p className="text-sm text-gray-900">{user.last_name}</p>
				</div>
				<div className="col-span-2">
					<div className="block text-sm font-medium text-gray-700 mb-1">Email</div>
					<p className="text-sm text-gray-900">{user.email}</p>
				</div>
				<div>
					<div className="block text-sm font-medium text-gray-700 mb-1">Status</div>
					<div className="flex gap-2">
						<Badge color={user.approval_status === "approved" ? "green" : user.approval_status === "rejected" ? "red" : "yellow"}>{user.approval_status}</Badge>
						{user.disabled_at && user.approval_status === "approved" && <Badge color="red">Disabled</Badge>}
					</div>
				</div>
				<div>
					<div className="block text-sm font-medium text-gray-700 mb-1">Registered</div>
					<p className="text-sm text-gray-900">{formatDate(user.created_at, "PPpp")}</p>
				</div>
				{user.approved_by && (
					<>
						<div>
							<div className="block text-sm font-medium text-gray-700 mb-1">Approved By</div>
							<p className="text-sm text-gray-900">
								{user.approved_by.first_name} {user.approved_by.last_name}
							</p>
						</div>
						<div>
							<div className="block text-sm font-medium text-gray-700 mb-1">Approved At</div>
							<p className="text-sm text-gray-900">{user.approved_at ? formatDate(user.approved_at, "PPpp") : ""}</p>
						</div>
					</>
				)}
				{user.approval_notes && (
					<div className="col-span-2">
						<div className="block text-sm font-medium text-gray-700 mb-1">Notes</div>
						<p className="text-sm text-gray-900">{user.approval_notes}</p>
					</div>
				)}
			</div>
			<div className="flex justify-end">
				<Button onClick={() => modal.closeModal()}>Close</Button>
			</div>
		</div>
	);
}

export default UserApprovalsPage;
